import { Suspense, type ComponentType } from 'react';
import { AlertCircle } from 'lucide-react';
import { Skeleton } from '@reflex/ui/components/ui/skeleton';
import { useResourcePreviewRegistration } from './useResourcePreviewRegistration';
import type { ResourcePreviewProps } from '@reflex/plugin-api';

interface ResourcePreviewLoaderProps extends ResourcePreviewProps {
  /** Stable kind discriminator, e.g. `'personas'`. */
  kind: string;
  /**
   * Optional fallback name shown if no preview registration is found
   * (e.g. when the owning plugin has been disabled).
   */
  fallbackName?: string;
}

/**
 * Resolves a registered preview component for `kind` and renders it inside a
 * Suspense boundary. If no component is registered, a small "no preview
 * available" frame is shown instead — preserving layout in the popover.
 */
export function ResourcePreview({ kind, id, fallbackName }: ResourcePreviewLoaderProps) {
  const registration = useResourcePreviewRegistration(kind);

  if (!registration) {
    return (
      <div
        className="flex items-center gap-2 text-[12px] text-muted-foreground"
        data-testid="resource-preview-missing"
      >
        <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate">
          No preview available{fallbackName ? ` for ${fallbackName}` : ''}.
        </span>
      </div>
    );
  }

  const Component = registration.component as ComponentType<ResourcePreviewProps>;
  return (
    <Suspense fallback={<ResourcePreviewSkeleton />}>
      <Component id={id} />
    </Suspense>
  );
}

function ResourcePreviewSkeleton() {
  return (
    <div className="space-y-3" data-testid="resource-preview-skeleton">
      <div className="flex items-center gap-2">
        <Skeleton className="h-8 w-8 rounded-md" />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-3.5 w-3/4" />
          <Skeleton className="h-2.5 w-1/2" />
        </div>
      </div>
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-5/6" />
      <Skeleton className="h-7 w-full" />
    </div>
  );
}
